"use client"

import { replaceRichTextLinks } from "@/utils/replaceRichTextLinks"
import styled from "@emotion/styled"
import { Box, Typography } from "@mui/material"
import Image from "next/image"
import Link from "next/link"
import { useInView } from "react-intersection-observer"
import {
  sizes,
  transitionDelayTime,
  useInViewConfig,
  useIsDesktop,
  useTransitionUpStyles,
} from "ui"
import ButtonWithArrow from "ui/common/bit/ButtonWithArrow"
import LottiePlayer from "ui/common/bit/LottiePlayer"
import { isLottieFile } from "ui/common/bit/isLottieFile"
import { colors } from "ui/theme/colors"
import { mediaQueries } from "ui/theme/mediaQueries"
import { getNonUmbracoUrl, getUmbracoMediaUrl } from "utils"
import { sendBlockEvent } from "utils/gtm"
import BlockWrapper from "../common/BlockWrapper"

const StyledBGImageContainer = styled(Box)`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  height: auto;
  img {
    object-fit: contain;
  }
  ${mediaQueries.mobile} {
    margin-bottom: -65px;
  }
  ${mediaQueries.tablet} {
    margin-bottom: -41px;
  }
  ${mediaQueries.desktopM} {
    position: absolute;
    inset: 0 auto auto 0;
    width: 650px;
    height: 100%;
  }
`

const StyledMainImageContainer = styled(Box)`
  img {
    object-fit: contain;
  }
  ${mediaQueries.mobile} {
    height: 330px;
    width: 152px;
  }
  ${mediaQueries.tablet} {
    height: 330px;
    width: 152px;
  }

  ${mediaQueries.desktopM} {
    position: absolute;
    height: 400px;
    width: 518px;
    inset: 100px auto 0 180px;
  }
  ${mediaQueries.desktopXl} {
  }
`
const StyledTextsContainer = styled(Box)`
  display: flex;
  flex-direction: column;
`
const StyledTitle = styled(Typography)<{ component?: string }>`
  text-align: start;
  max-width: 100%;
  margin-bottom: ${sizes.baseDouble};
  color: ${colors.darkBlue};
  ${mediaQueries.mobile} {
  }
  ${mediaQueries.tablet} {
    max-width: 50%;
  }
  ${mediaQueries.desktopM} {
    margin-bottom: 16px;
  }
`

const StyledSubtitle = styled(Typography)`
  text-align: start;
`

const StyledBullets = styled(Box)`
  color: ${colors.black};
  font-size: 18px;

  ul {
    display: flex;
    flex-direction: column;
    list-style-type: none;
    padding: 0;
    gap: 12px;
    li {
      position: relative;
      display: flex;
      align-items: center;
      text-align: start;
      font-weight: 400;
      gap: 12px;
      &::before {
        content: url("/icons/check.svg");
      }
    }
  }
`
const StyledButtonsContainer = styled(Box)`
  margin-top: ${sizes.baseSingle};
  ${mediaQueries.desktopM} {
    margin-top: ${sizes.baseTriple};
  }
`
const StyledButtonWithArrow = styled(ButtonWithArrow)<{ bgcolor?: string }>`
  background-color: ${(props) => props.bgcolor};
  height: 56px;
  a {
    text-decoration: none;
  }
`
const GoComponent = ({ data, id }: any) => {
  const transformedData = data
  const isDesktop = useIsDesktop()
  const { ref, inView } = useInView(useInViewConfig)
  const ditransitiveStyles = useTransitionUpStyles(inView, transitionDelayTime)

  return (
    transformedData.checkbox.value === true && (
      <BlockWrapper
        id={id}
        style={{ justifyContent: "center" }}
        bgColor={colors.turquoise}
      >
        <StyledTextsContainer ref={ref} style={{ ...ditransitiveStyles }}>
          <StyledTitle variant="h3" component="p" color={colors.black}>
            {transformedData.subtitle.value}
          </StyledTitle>
          <StyledSubtitle variant="h2" color={colors.black}>
            {transformedData.title.value}
          </StyledSubtitle>
          {transformedData?.bulets ? (
            <StyledBullets
              dangerouslySetInnerHTML={{
                __html: replaceRichTextLinks(transformedData?.bulets?.richText),
              }}
            />
          ) : (
            <Typography color={colors.black} fontWeight={400} variant="body2">
              {transformedData.text.value}
            </Typography>
          )}
          {transformedData.boldText ? (
            <Typography color={colors.black} fontWeight={700} variant="body2">
              {transformedData.boldText.value}
            </Typography>
          ) : (
            <></>
          )}
          {transformedData.appstoreLink || transformedData.googlePlayLink ? (
            <StyledButtonsContainer>
              {transformedData.appstoreLink?.links[0] && (
                <Link
                  href={getNonUmbracoUrl(
                    transformedData.appstoreLink.links[0].url,
                  )}
                  target={transformedData.appstoreLink.links[0].target}
                  rel="nofollow"
                  onClick={() => {
                    sendBlockEvent({
                      blockId: "4",
                      componentName: "נהג מזדמן",
                      title: transformedData.title?.value,
                      buttonName: "app store",
                      productName: data?.formDefaultInsuranceType,
                    })
                  }}
                >
                  {transformedData.appstoreLogo?.mediaItems &&
                    transformedData.appstoreLogo?.mediaItems[0]?.url && (
                      <Image
                        width={131}
                        height={40}
                        style={{ objectFit: "cover" }}
                        alt={"appstoreLogo"}
                        src={getUmbracoMediaUrl(
                          transformedData.appstoreLogo?.mediaItems[0]?.url,
                        )}
                      />
                    )}
                </Link>
              )}
              {transformedData.googlePlayLink?.links[0] && (
                <Link
                  href={getNonUmbracoUrl(
                    transformedData.googlePlayLink.links[0].url,
                  )}
                  target={transformedData.googlePlayLink.links[0].target}
                  rel="nofollow"
                  onClick={() => {
                    sendBlockEvent({
                      blockId: "4",
                      componentName: "נהג מזדמן",
                      title: transformedData.title?.value,
                      buttonName: "google play",
                      productName: data?.formDefaultInsuranceType,
                    })
                  }}
                >
                  {transformedData?.googlePlayLogo?.mediaItems && (
                    <Image
                      width={131}
                      height={40}
                      alt="googlePlayLogo"
                      style={{ objectFit: "cover" }}
                      src={getUmbracoMediaUrl(
                        transformedData.googlePlayLogo?.mediaItems[0]?.url,
                      )}
                    />
                  )}
                </Link>
              )}
            </StyledButtonsContainer>
          ) : (
            <StyledButtonWithArrow
              bgcolor={colors.white}
              arrowName="arrow_lightTurquoise_black_line"
              onClick={() => {
                sendBlockEvent({
                  blockId: "4",
                  componentName: "נהג מזדמן",
                  title: transformedData.title?.value,
                  buttonName: transformedData.link?.links[0]?.name,
                  productName: data?.formDefaultInsuranceType,
                })
              }}
            >
              <Link
                href={getNonUmbracoUrl(transformedData.link?.links[0]?.url)}
                target={transformedData.link?.links[0]?.target}
              >
                <Typography
                  variant="body1"
                  fontWeight={700}
                  color={colors.black}
                >
                  {transformedData.link?.links[0]?.name}
                </Typography>
              </Link>
            </StyledButtonWithArrow>
          )}
        </StyledTextsContainer>
        <StyledBGImageContainer ref={ref} style={{ ...ditransitiveStyles }}>
          <Image
            priority
            src={
              isDesktop
                ? "/hachsharaProtect.svg"
                : "/hachsharaProtectMobile.svg"
            }
            alt="hachshara protect logo"
            fill
          />
          {transformedData?.desktopImage?.mediaItems &&
            transformedData?.desktopImage?.mediaItems[0]?.url && (
              <StyledMainImageContainer>
                {isLottieFile(
                  getUmbracoMediaUrl(
                    transformedData.desktopImage.mediaItems[0]?.url,
                  ),
                ) ? (
                  <LottiePlayer
                    lottieUrl={getUmbracoMediaUrl(
                      transformedData.desktopImage.mediaItems[0]?.url,
                    )}
                    playOnce={true}
                    autoplay={true}
                    loop={false}
                    ariaLabel={transformedData.title.value}
                  />
                ) : (
                  <Image
                    src={getUmbracoMediaUrl(
                      transformedData.desktopImage.mediaItems[0]?.url,
                    )}
                    alt={transformedData.title.value}
                    sizes="100vw"
                    fill
                  />
                )}
              </StyledMainImageContainer>
            )}
        </StyledBGImageContainer>
      </BlockWrapper>
    )
  )
}

export default GoComponent
